import { X, Plus, Loader2, Clapperboard } from "lucide-react";

function Card({ title, children }) {
  return <div className="rounded-xl border border-white/10 p-3"><div className="text-[10px] font-mono uppercase text-slate-500 mb-1">{title}</div><div className="text-xs text-slate-300">{children}</div></div>;
}

function List({ items }) {
  return <ul className="space-y-1">{(items || []).map((x, i) => <li key={i}>• {typeof x === "object" ? JSON.stringify(x) : String(x)}</li>)}</ul>;
}

function KV({ obj }) {
  return <div>{Object.entries(obj || {}).map(([k, v]) => <div key={k}><span className="text-slate-500">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}</div>)}</div>;
}

function Recommendations({ items, onCreateIdea, onGenerateVideo, busy }) {
  return (
    <div className="space-y-2">
      {items.map((r, i) => (
        <div key={i} data-testid={`fc-rec-${i}`} className="rounded-xl border border-white/10 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm font-semibold">{i + 1}. {r.title}</div>
            <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${r.evidence_type === "measured" ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>{r.evidence_type || "inferred"}</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">{r.reason}</div>
          <div className="text-[11px] text-slate-500 mt-1"><b>Hook:</b> {r.hook} · <b>Format:</b> {r.format} · <b>Offer:</b> {r.suggested_offer_type}</div>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <button data-testid={`fc-rec-save-${i}`} onClick={() => onCreateIdea(r.title, r.reason)} className="text-xs text-cyan-300 hover:underline inline-flex items-center gap-1"><Plus size={11} /> Add to Idea Engine</button>
            {onGenerateVideo && <button data-testid={`fc-rec-generate-${i}`} disabled={!!busy} onClick={() => onGenerateVideo(r.title, r.reason, i)} className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white inline-flex items-center gap-1 disabled:opacity-50">{busy === "gv-r" + i ? <Loader2 size={12} className="animate-spin" /> : <Clapperboard size={12} />} GENERATE VIDEO</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Pattern({ d }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Card title="Pattern"><KV obj={d.pattern} /></Card>
      <Card title="Why this worked">{d.why_this_worked}</Card>
      <Card title="How we create an original version"><KV obj={d.original_version} /></Card>
    </div>
  );
}

function Learn({ d }) {
  if (d.status === "INSUFFICIENT_DATA") return <div data-testid="fc-learn-insufficient" className="text-xs text-amber-300">{d.message}</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Card title="Do more of this"><List items={d.do_more} /></Card>
      <Card title="Test this"><List items={d.test} /></Card>
      <Card title="Stop doing this"><List items={d.stop} /></Card>
      <div className="md:col-span-3 text-[10px] text-slate-500">Based on {d.based_on_rows} measured entries · {d.evidence_notes}</div>
    </div>
  );
}

function Generic({ d }) {
  const entries = Object.entries(d || {}).filter(([k]) => !["_id", "user_id"].includes(k));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {entries.map(([k, v]) => (
        <Card key={k} title={k}>{Array.isArray(v) ? <List items={v} /> : typeof v === "object" && v !== null ? <KV obj={v} /> : String(v)}</Card>
      ))}
    </div>
  );
}

function Body({ d, onCreateIdea, onGenerateVideo, busy }) {
  if (!d) return null;
  if (Array.isArray(d.recommendations)) return <Recommendations items={d.recommendations} onCreateIdea={onCreateIdea} onGenerateVideo={onGenerateVideo} busy={busy} />;
  if (d.pattern) return <Pattern d={d} />;
  if (d.do_more || d.status === "INSUFFICIENT_DATA") return <Learn d={d} />;
  if (Array.isArray(d.items)) return <div className="text-xs text-slate-400">{d.items.length} items generated — see the relevant tab.</div>;
  if (d.download) return <a href={d.download} className="text-xs text-cyan-300 hover:underline">Download editor package</a>;
  return <Generic d={d} />;
}

export function ResultView({ out, onClose, onCreateIdea, onGenerateVideo, busy }) {
  const d = out.data || {};
  const isBrain = !!d.action;
  return (
    <div data-testid="fc-output" className="glass rounded-2xl p-4 relative">
      <button data-testid="fc-output-close" onClick={onClose} className="absolute top-3 right-3 text-slate-500 hover:text-white"><X size={14} /></button>
      <div className="font-display font-semibold text-sm mb-3">Vectra Brain result{isBrain && <span className="text-cyan-300 font-mono text-xs ml-2">action: {d.action}</span>}</div>
      <Body d={isBrain ? d.result : d} onCreateIdea={onCreateIdea} onGenerateVideo={onGenerateVideo} busy={busy} />
    </div>
  );
}
